import type { ReceivedInvoiceAiSuggestStatusProperty } from '@/context/services/invoice/type';
import {
  isAiSuggestFailed,
  isAiSuggestSuccessWithoutReference,
} from '@/features/invoices/utils/aiSuggestStatus';
import classnames from 'classnames/bind';
import { FC, memo } from 'react';
import { AiSuggestFailureLabel } from './AiSuggestFailureLabel';
import { AiSuggestSuccessLabel } from './AiSuggestSuccessLabel';
import styles from './AiSuggestSummaryBanner.module.scss';
import { ReferencedReportLink } from './ReferencedReportLink';

const cx = classnames.bind(styles);

export type AiSuggestSummaryBannerInvoiceAiSuggestion = {
  status: ReceivedInvoiceAiSuggestStatusProperty;
  referenced_report?: { detail_path: string; number: string } | null;
} | null;

export type AiSuggestSummaryBannerProps = {
  invoiceAiSuggestion?: AiSuggestSummaryBannerInvoiceAiSuggestion;
};

const InnerAiSuggestSummaryBanner: FC<AiSuggestSummaryBannerProps> = ({
  invoiceAiSuggestion,
}) => {
  if (!invoiceAiSuggestion) {
    return null;
  }

  const { status, referenced_report } = invoiceAiSuggestion;
  const referenceNumber = referenced_report?.number;
  const isFailure = isAiSuggestFailed(status);
  const isSuccessWithoutReference =
    isAiSuggestSuccessWithoutReference(invoiceAiSuggestion);

  if (!referenceNumber && !isFailure && !isSuccessWithoutReference) {
    return null;
  }

  return (
    <div className={cx('banner')}>
      {referenceNumber && (
        <ReferencedReportLink
          number={referenceNumber}
          detailPath={referenced_report?.detail_path}
        />
      )}
      {isSuccessWithoutReference && <AiSuggestSuccessLabel />}
      {isFailure && <AiSuggestFailureLabel />}
    </div>
  );
};

InnerAiSuggestSummaryBanner.displayName = 'InnerAiSuggestSummaryBanner';

export const AiSuggestSummaryBanner: FC<AiSuggestSummaryBannerProps> = memo(
  InnerAiSuggestSummaryBanner
);
AiSuggestSummaryBanner.displayName = 'AiSuggestSummaryBanner';
